import environment from '../../../.configs/app/environment.js'
import sessionStore from '../../stores/session-store.js'
import { combineAbortSignals } from '../utils/abort-signal-utils.js'
import { getKnownSimClockStateId } from '../utils/app-clock.js'
import { getLocaleFromPathname } from '../utils/locale-path.js'
import { observeQaFaultsFromResponse } from '../utils/qa-fault-fired-events.js'
import {
    applySimClockDriftFromResponse,
    buildSimClockDriftRequestHeaders,
    isSimClockDriftEnabled,
} from '../utils/sim-clock-drift.js'
import {
    REJECTED_SESSION_OUTCOME,
    hasUsableToken,
    isRejectedSessionResponse,
} from '../utils/session-rejection.js'

const mergeSimClockDriftHeaders = (headers = {}) => {
    if (!isSimClockDriftEnabled()) {
        return headers
    }
    return {
        ...headers,
        ...buildSimClockDriftRequestHeaders(getKnownSimClockStateId()),
    }
}

/**
 * TENANT-5624: the API resolves `request.locale` from this header first and returns user-generated
 * content (listing text, reviews, host bios) translated into it. The UI locale is the URL's locale
 * segment — the same derivation `LocaleProvider.locale()` uses — so every request carries the
 * language the visitor is reading the page in, not a stored preference that may lag behind it.
 */
export const LOCALE_REQUEST_HEADER = 'X-Locale'

const withLocaleHeader = (headers) => ({
    ...headers,
    [LOCALE_REQUEST_HEADER]: getLocaleFromPathname(window.location.pathname),
})

/**
 * TENANT-5664: an optional per-request deadline. `timeoutMs` aborts the request with a
 * `TimeoutError` once it elapses; without it the request has no client deadline, as before.
 */
const buildTimeoutSignal = (timeoutMs) => {
    if (timeoutMs === undefined) return
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
        throw new TypeError(`BaseAPI: timeoutMs must be a positive number of milliseconds, got ${timeoutMs}.`)
    }
    return AbortSignal.timeout(timeoutMs)
}

/**
 * The request's abort signal: the caller's own `signal` (to cancel a superseded request), the
 * `timeoutMs` deadline, both combined, or none.
 */
const buildRequestSignal = ({
    signal,
    timeoutMs,
}) => {
    if (signal !== undefined && !(signal instanceof AbortSignal)) {
        throw new TypeError('BaseAPI: signal must be an AbortSignal.')
    }
    const timeoutSignal = buildTimeoutSignal(timeoutMs)
    if (!signal) return timeoutSignal
    if (!timeoutSignal) return signal
    return combineAbortSignals([
        signal,
        timeoutSignal,
    ])
}

const observeSimClockDrift = (response) => {
    applySimClockDriftFromResponse(response)
    return response
}

// TENANT-5416: the API flags a request during which an armed QA fault fired via an optional
// response header; surface it to the QA overlay. Runs before the drift check, which may throw.
const observeQaFaults = (response) => {
    observeQaFaultsFromResponse(response)
    return response
}

/**
 * TENANT-5504: who clears the stored session when the API rejects it.
 *
 * The data layer detects the rejection; the session itself belongs to `SessionService`, which
 * registers here at module load (the data layer must not import a service). Set once per app.
 */
let rejectedSessionHandler

class BaseAPI {
    /**
     * @param {(rejectedToken: string) => Promise<string>} handler - resolves to a `REJECTED_SESSION_OUTCOME`.
     */
    static setRejectedSessionHandler (handler) {
        if (typeof handler !== 'function') {
            throw new TypeError('BaseAPI.setRejectedSessionHandler requires a function.')
        }
        rejectedSessionHandler = handler
    }

    static async fetch (endpoint, options = {}) {
        if (environment.api.base === 'mock') {
            console.log('BaseAPI.fetch API_MODE === mock', endpoint)
            return this.fetchMock(endpoint)
        }
        const response = await fetch(environment.api.base + endpoint, {
            body: JSON.stringify(options.body),
            headers: withLocaleHeader(mergeSimClockDriftHeaders({
                'Content-Type': 'application/json',
                ...options.headers,
            })),
            // Lets a fire-and-forget report outlive the page that sent it.
            ...(options.keepalive && {
                keepalive: true,
            }),
            method: options.method,
            signal: buildRequestSignal(options),
        })
        return observeSimClockDrift(observeQaFaults(response))
    }

    static async fetchSecure (endpoint, options = {}) {
        const token = await sessionStore.getAccessToken()

        // Attach the Authorization header only when we have a real token.
        // When there's no usable session we still issue the request so
        // `security: []` endpoints work for anonymous visitors; protected
        // endpoints will get a real server-issued 401 (no `Bearer null`).
        const headers = withLocaleHeader(mergeSimClockDriftHeaders({
            ...(hasUsableToken(token) && {
                Authorization: 'Bearer ' + token,
            }),
            ...options.headers || (options.body && {
                'Content-Type': 'application/json',
            }),
        }))

        const response = await fetch(environment.api.base + endpoint, {
            body: options.multipart ? options.body : JSON.stringify(options.body),
            headers,
            method: options.method,
            signal: buildRequestSignal(options),
        })
        observeQaFaults(response)
        observeSimClockDrift(response)

        // A stored session the API no longer honours (TENANT-5504). Awaited so that by the time
        // the caller's `fetchResponseHandler` throws, the session is already cleared and a
        // caller that owns its own sign-in prompt sees the signed-out state.
        if (isRejectedSessionResponse({
            endpoint,
            sentToken: token,
            status: response.status,
        })) {
            if (!rejectedSessionHandler) {
                throw new Error(`BaseAPI.fetchSecure: ${endpoint} rejected the stored session (401) but no rejected-session handler is registered — SessionService must be loaded before authenticated calls.`)
            }
            const outcome = await rejectedSessionHandler(token)
            // The rejected token was stale: a newer session was signed in while this request was
            // in flight. Re-issue it ONCE with that session rather than failing a request the
            // guest is now authorised to make; a second rejection is returned as it is.
            if (outcome === REJECTED_SESSION_OUTCOME.NEWER_SESSION_KEPT && !options.isRetryWithNewerSession) {
                return this.fetchSecure(endpoint, {
                    ...options,
                    isRetryWithNewerSession: true,
                })
            }
        }

        return response
    }

    static async fetchMock (url) {
        console.log('BaseAPI.fetchMock')
        const [
            path,
            queryParameters,
        ] = url.split('?')
        const mockFilePath = `/mocks/api${path.replaceAll(/[^\w./-]/g, '_')}_${queryParameters.replaceAll(/[^\w.-]/g, '_')}.js`

        console.log('BaseAPI.fetchMock', mockFilePath)

        try {
            const mockData = await import(mockFilePath)
            console.log('BaseAPI.fetchMock', mockData)
            return mockData.default
        } catch (error) {
            console.error('BaseAPI.fetchMock Failed to load mock data:', error)
            throw error
        }
    }
}

export default BaseAPI